import React, { useState, useEffect } from 'react';

const QuickCustomerForm = ({ onSubmit, onCancel, loading, initialData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    company: ''
  });

  const [errors, setErrors] = useState({});

  // Prefill when editing (runs when initialData changes)
  useEffect(() => {
    if (!initialData) return;
    const fullAddr = initialData.address || '';
    const cpMatch = fullAddr.match(/\b(\d{5})\b\s+([^,\n]+)$/);
    setFormData(prev => ({
      ...prev,
      name: initialData.name || '',
      email: initialData.email || '',
      phone: initialData.phone || '',
      company: initialData.company || '',
      address: fullAddr,
      postalCode: cpMatch ? (cpMatch[1] || '') : '',
      city: cpMatch ? ((cpMatch[2] || '').trim()) : '',
    }));
  }, [initialData?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        address: [formData.address, formData.postalCode, formData.city].filter(Boolean).join(' ')
      };
      await onSubmit(payload);
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content quick-customer-modal">
        <div className="modal-header">
          <h3>{initialData?.id ? 'Modifier le client' : 'Nouveau Client Rapide'}</h3>
          <button className="close-btn" onClick={onCancel} type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="quick-customer-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Nom *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={errors.name ? 'error' : ''} placeholder="Nom du client" autoFocus />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company">Entreprise</label>
              <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Nom de l'entreprise" />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={errors.email ? 'error' : ''} placeholder="email@exemple.com" />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Téléphone</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="06 12 34 56 78" />
            </div>

            <div className="form-group full-width">
              <label htmlFor="address">Adresse</label>
              <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} placeholder="Adresse complète" />
            </div>

            <div className="form-group">
              <label htmlFor="postalCode">Code postal</label>
              <input type="text" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="75000" />
            </div>

            <div className="form-group">
              <label htmlFor="city">Ville</label>
              <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Paris" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={loading} className="btn-primary">{loading ? (initialData?.id ? 'Mise à jour...' : 'Création...') : (initialData?.id ? 'Mettre à jour' : 'Créer Client')}</button>
          </div>
        </form>
        <style>{`
          .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
          .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 800px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
          .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 0 20px; border-bottom: 1px solid var(--admin-border); margin-bottom: 16px; }
          .modal-header h3 { margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--admin-text); }
          .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 6px; border-radius: 6px; transition: all .2s ease; }
          .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
          .quick-customer-form { padding: 20px; }
          .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px,1fr)); gap: 16px; margin-bottom: 16px; }
          .form-group { display: flex; flex-direction: column; gap: 6px; }
          .form-group label { font-weight: 500; color: var(--admin-text); font-size: .9rem; }
          .form-group input, .form-group select { padding: 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 8px; font-size: .9rem; transition: border-color .2s ease, box-shadow .2s ease; }
          .form-group input:focus, .form-group select:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .form-group input.error { border-color: #ef4444; }
          .error-message { color: #ef4444; font-size: .8rem; }
          .full-width { grid-column: 1 / -1; }
          .form-actions { display: flex; justify-content: flex-end; gap: 10px; padding-top: 16px; border-top: 1px solid var(--admin-border); }
          .btn-secondary { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-secondary:hover { background: rgba(255,255,255,0.06); }
          .btn-primary { background: var(--admin-accent); color: #fff; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-primary:disabled { opacity: .6; cursor: not-allowed; }
          @media (max-width: 640px) { .modal-content { width: 95vw; } }
        `}</style>
      </div>
    </div>
  );
};

export default QuickCustomerForm;
